import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';

const TooltipCustom = ({ 
  text, 
  children, 
  position = 'bottom-center',
  width = 'w-64',
  delay = 200,
  className = ''
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const triggerRef = useRef(null);
  const timeoutRef = useRef(null);

  useEffect(() => {
    if (isVisible) {
      timeoutRef.current = setTimeout(() => {
        setShowTooltip(true);
      }, delay);
    } else {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      setShowTooltip(false);
    }
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [isVisible, delay]);

  const getTooltipStyle = () => {
    if (!triggerRef.current) return {};
    const rect = triggerRef.current.getBoundingClientRect();
    const tooltipWidth = 224; // w-56 = 224px
    
    let style = {
      position: 'fixed',
      zIndex: 9999
    };

    // Auto-detect best position based on available space
    if (position === 'auto' || position === 'bottom-auto') {
      style.top = `${rect.bottom + 8}px`;
      
      // Check available space on right
      const spaceRight = window.innerWidth - rect.right;
      // Check available space on left
      const spaceLeft = rect.left;
      
      if (spaceRight >= tooltipWidth) {
        // Enough space on right - align left edge with icon
        style.left = `${rect.left}px`;
      } else if (spaceLeft >= tooltipWidth) {
        // Not enough on right, but enough on left - align right edge with icon
        style.right = `${window.innerWidth - rect.right}px`;
      } else {
        // Not enough space either side - center and let it overflow
        style.left = `${rect.left + rect.width / 2}px`;
        style.transform = 'translateX(-50%)';
      }
    } else {
      // Manual positioning
      switch (position) {
        case 'bottom-center':
          style.top = `${rect.bottom + 8}px`;
          style.left = `${rect.left + rect.width / 2}px`;
          style.transform = 'translateX(-50%)';
          break;
        case 'bottom-right':
          style.top = `${rect.bottom + 8}px`;
          style.right = `${window.innerWidth - rect.right}px`;
          break;
        case 'bottom-left':
          style.top = `${rect.bottom + 8}px`;
          style.left = `${rect.left}px`;
          break;
        case 'top-center':
          style.bottom = `${window.innerHeight - rect.top + 8}px`;
          style.left = `${rect.left + rect.width / 2}px`;
          style.transform = 'translateX(-50%)';
          break;
        default:
          style.top = `${rect.bottom + 8}px`;
          style.left = `${rect.left + rect.width / 2}px`;
          style.transform = 'translateX(-50%)';
      }
    }
    
    return style;
  };

  return (
    <div 
      ref={triggerRef}
      onMouseEnter={() => setIsVisible(true)}
      onMouseLeave={() => setIsVisible(false)}
      className="inline-block"
    >
      {children}
      {showTooltip && createPortal(
        <div 
          style={getTooltipStyle()} 
          className={`${width} bg-gray-800 text-white text-xs rounded px-3 py-2 shadow-lg pointer-events-none ${className}`}
        >
          {text}
        </div>,
        document.body
      )}
    </div>
  );
};

export default TooltipCustom;

